import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { motion } from 'framer-motion';
import type { UploadAreaProps } from '../reducers/types';
import ErrorMessage from './ErrorMessage';
import FileTypeIndicator from './FileTypeIndicator';
import { UI_FORMAT_CATEGORIES } from '../utils/constants';

/**
 * Upload Area Component
 * 
 * Displays the main upload area where users can drag and drop or click to select files.
 * Shows format indicators, drag hints, and error messages.
 * 
 * @param props Component props
 * @returns JSX Element
 */
const UploadArea: React.FC<UploadAreaProps> = t0 => {
  const $ = _c(18);
  const {
    visualState,
    error,
    hasRecoveryOption,
    onRetry
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="tw-absolute tw-inset-[10px] tw-border-2 tw-border-dashed tw-border-iron-700 group-hover:tw-border-primary-500/30 tw-rounded-lg tw-transition-all tw-duration-300" />;
    t2 = <div className="tw-absolute tw-inset-[10px] tw-border tw-border-dashed tw-border-iron-600/20 group-hover:tw-border-iron-500/30 tw-rounded-lg tw-animate-pulse tw-transition-all tw-duration-300" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="tw-absolute tw-inset-0 tw-pointer-events-none tw-opacity-5"><div className="tw-grid tw-grid-cols-8 tw-h-full">{Array(32).fill(0).map(_temp)}</div></div>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-tr tw-from-primary-500/20 tw-to-transparent tw-rounded-full tw-animate-pulse" />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="tw-absolute tw-inset-0 tw-border-2 tw-border-dashed tw-border-iron-700 tw-rounded-full tw-animate-spin" style={{
      animationDuration: "10s"
    }} />;
    t6 = <div className="tw-absolute tw-inset-4 tw-border tw-border-iron-600 tw-rounded-full" />;
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="tw-relative tw-mb-2"><div className="tw-w-32 tw-h-32 tw-relative">{t4}{t5}{t6}<div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center"><span className="tw-text-iron-400 tw-text-sm tw-font-medium group-hover:tw-text-primary-300 tw-transition-colors tw-duration-300">Select Art</span></div></div></div>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="tw-flex tw-items-center tw-justify-center tw-mb-8 tw-transition-all tw-duration-300"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="tw-w-4 tw-h-4 tw-mr-1.5 tw-text-iron-500 group-hover:tw-text-primary-400 tw-transition-colors tw-duration-300" aria-hidden="true"><path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" /></svg><span className="tw-text-iron-500 tw-text-xs group-hover:tw-text-iron-300 tw-transition-colors tw-duration-300">Drag and drop file here</span></div>;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="tw-absolute tw-bottom-6 tw-left-6 tw-right-6"><div className="tw-flex tw-justify-center tw-gap-3 tw-text-xs tw-text-iron-500">{UI_FORMAT_CATEGORIES.map(_temp2)}</div></div>;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== visualState) {
    t10 = visualState === "processing" && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-iron-950/70" aria-live="polite"><div className="tw-flex tw-flex-col tw-items-center tw-gap-3"><div className="tw-h-12 tw-w-12 tw-border-t-2 tw-border-b-2 tw-border-primary-500 tw-rounded-full tw-animate-spin" /><span className="tw-text-iron-300 tw-font-medium">Processing file...</span><span className="tw-text-iron-400 tw-text-xs">This may take longer for large files (up to 100MB)</span></div></motion.div>;
    $[9] = visualState;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== error || $[12] !== hasRecoveryOption || $[13] !== onRetry) {
    t11 = error && <ErrorMessage error={error} showRetry={hasRecoveryOption} onRetry={onRetry} />;
    $[11] = error;
    $[12] = hasRecoveryOption;
    $[13] = onRetry;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t10 || $[16] !== t11) {
    t12 = <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-6">{t1}{t2}{t3}{t7}{t8}{t9}{t10}{t11}</div>;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  return t12;
};
export default UploadArea;
function _temp(_, i) {
  return <div key={i} className="tw-border-[0.5px] tw-border-iron-400/40" />;
}
function _temp2(format) {
  return <FileTypeIndicator key={format} format={format} />;
}